import React, { useState, useRef, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { Upload, Trash2, TrendingUp, DollarSign, Wallet, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const MESES_CORTO = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

// Secciones del CSV → categoría interna
const SECCIONES = {
  'Ingresos Pesos': { section: 'ingreso', currency: 'UYU' },
  'Ingresos Dolares': { section: 'ingreso', currency: 'USD' },
  'Ingresos Dólares': { section: 'ingreso', currency: 'USD' },
  'Salarios': { section: 'salario', currency: 'UYU' },
  'Gastos Fijos': { section: 'gasto', currency: 'UYU' },
};
const SECCIONES_FIN = ['Resumen Ejecutivo']; // a partir de acá, ignorar

function parseMonto(s) {
  if (!s) return 0;
  let t = String(s).replace(/\$/g, '').replace(/\t/g, '').replace(/\s/g, '').trim();
  if (!t) return 0;
  t = t.replace(/\./g, '').replace(',', '.');
  const n = parseFloat(t);
  return isNaN(n) ? 0 : n;
}

// Parsea el CSV (formato Numbers, separador ;) a registros
function parseCSV(text) {
  const lines = text.split(/\r?\n/);
  const records = [];
  let seccion = null;
  for (const line of lines) {
    if (!line.trim()) continue;
    const cols = line.split(';');
    const col1 = (cols[0] || '').trim();
    const col2 = (cols[1] || '').trim();
    if (SECCIONES_FIN.includes(col1)) { seccion = null; continue; }
    if (SECCIONES[col1]) {
      seccion = col1;
      if (!col2) continue; // fila de título sin dato
    } else if (col1) {
      seccion = null; // sección no reconocida
      continue;
    }
    if (!seccion) continue;
    const concepto = col2;
    if (!concepto) continue;
    const { section, currency } = SECCIONES[seccion];
    for (let i = 0; i < 12; i++) {
      const idx = 2 + i;
      if (idx < cols.length) {
        const monto = parseMonto(cols[idx]);
        if (monto !== 0) records.push({ section, currency, concept: concepto, month: i + 1, amount: monto });
      }
    }
  }
  return records;
}

const fmt = (n, cur) => {
  const s = Math.round(n).toLocaleString('es-UY');
  return cur === 'USD' ? `U$S ${s}` : `$ ${s}`;
};

export default function FinancesPage() {
  const { finances, importFinances, deleteFinanceYear, currentUser, showToast } = useApp();
  const fileRef = useRef();
  const [importYear, setImportYear] = useState(new Date().getFullYear());
  const [preview, setPreview] = useState(null); // {records, resumen}
  const [importing, setImporting] = useState(false);

  // años disponibles
  const years = useMemo(() => [...new Set(finances.map(f => Number(f.year)))].sort((a, b) => b - a), [finances]);
  const [selectedYear, setSelectedYear] = useState(null);
  const yearActivo = selectedYear || years[0] || new Date().getFullYear();

  if (currentUser && currentUser.role !== 'admin') {
    return <div className="page"><div className="page-header"><h1 className="page-title">Finanzas</h1></div>
      <p style={{ color: 'var(--text-muted)' }}>No tenés permisos para ver este módulo.</p></div>;
  }

  // ─── Importación ───
  const onFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const text = await f.text();
    const records = parseCSV(text);
    if (records.length === 0) { alert('No se pudieron leer datos del archivo. ¿Es el CSV exportado de la planilla?'); return; }
    // resumen del preview
    const resumen = {};
    records.forEach(r => {
      const k = `${r.section}-${r.currency}`;
      resumen[k] = (resumen[k] || 0) + r.amount;
    });
    setPreview({ records, resumen });
    if (fileRef.current) fileRef.current.value = '';
  };

  const confirmarImport = async () => {
    if (!preview) return;
    setImporting(true);
    try {
      const res = await importFinances(importYear, preview.records);
      showToast(`Importados ${res.importados} registros del año ${importYear}`);
      setPreview(null);
      setSelectedYear(Number(importYear));
    } catch (err) {
      alert('Error al importar: ' + err.message);
    }
    setImporting(false);
  };

  const borrarAnio = async (y) => {
    if (window.confirm(`¿Borrar todos los datos financieros del año ${y}? Esta acción no se puede deshacer.`)) {
      await deleteFinanceYear(y);
      showToast(`Datos del año ${y} eliminados`, 'warning');
    }
  };

  // ─── Cálculos del año activo ───
  const datosAnio = useMemo(() => finances.filter(f => Number(f.year) === Number(yearActivo)), [finances, yearActivo]);

  const sumar = (section, currency) => datosAnio.filter(f => f.section === section && f.currency === currency).reduce((s, f) => s + Number(f.amount), 0);

  const ingresoUYU = sumar('ingreso', 'UYU');
  const ingresoUSD = sumar('ingreso', 'USD');
  const salariosUYU = sumar('salario', 'UYU');
  const gastosUYU = sumar('gasto', 'UYU');
  const rentaUYU = ingresoUYU - salariosUYU - gastosUYU;

  // Evolución mensual (pesos)
  const mensualUYU = MESES.map((m, i) => {
    const mes = i + 1;
    const ing = datosAnio.filter(f => f.section === 'ingreso' && f.currency === 'UYU' && f.month === mes).reduce((s, f) => s + Number(f.amount), 0);
    const sal = datosAnio.filter(f => f.section === 'salario' && f.month === mes).reduce((s, f) => s + Number(f.amount), 0);
    const gas = datosAnio.filter(f => f.section === 'gasto' && f.month === mes).reduce((s, f) => s + Number(f.amount), 0);
    return { mes: MESES_CORTO[i], Ingresos: Math.round(ing), Egresos: Math.round(sal + gas), Renta: Math.round(ing - sal - gas) };
  });
  // Evolución mensual (dólares)
  const mensualUSD = MESES.map((m, i) => {
    const mes = i + 1;
    const ing = datosAnio.filter(f => f.section === 'ingreso' && f.currency === 'USD' && f.month === mes).reduce((s, f) => s + Number(f.amount), 0);
    return { mes: MESES_CORTO[i], Ingresos: Math.round(ing) };
  });

  // Desglose por compañía (ingresos)
  const porCompaniaUYU = useMemo(() => {
    const m = {};
    datosAnio.filter(f => f.section === 'ingreso' && f.currency === 'UYU').forEach(f => { m[f.concept] = (m[f.concept] || 0) + Number(f.amount); });
    return Object.entries(m).map(([name, total]) => ({ name, total: Math.round(total) })).sort((a, b) => b.total - a.total);
  }, [datosAnio]);
  const porCompaniaUSD = useMemo(() => {
    const m = {};
    datosAnio.filter(f => f.section === 'ingreso' && f.currency === 'USD').forEach(f => { m[f.concept] = (m[f.concept] || 0) + Number(f.amount); });
    return Object.entries(m).map(([name, total]) => ({ name, total: Math.round(total) })).sort((a, b) => b.total - a.total);
  }, [datosAnio]);

  // Desglose de gastos y salarios
  const desgloseGastos = useMemo(() => {
    const m = {};
    datosAnio.filter(f => f.section === 'gasto' || f.section === 'salario').forEach(f => { m[f.concept] = (m[f.concept] || 0) + Number(f.amount); });
    return Object.entries(m).map(([name, total]) => ({ name, total: Math.round(total) })).sort((a, b) => b.total - a.total);
  }, [datosAnio]);

  // Comparación entre años (renta e ingresos en pesos)
  const comparacion = useMemo(() => years.slice().sort((a, b) => a - b).map(y => {
    const d = finances.filter(f => Number(f.year) === y);
    const ing = d.filter(f => f.section === 'ingreso' && f.currency === 'UYU').reduce((s, f) => s + Number(f.amount), 0);
    const sal = d.filter(f => f.section === 'salario').reduce((s, f) => s + Number(f.amount), 0);
    const gas = d.filter(f => f.section === 'gasto').reduce((s, f) => s + Number(f.amount), 0);
    return { year: String(y), Ingresos: Math.round(ing), Renta: Math.round(ing - sal - gas) };
  }), [finances, years]);

  const hayDatos = datosAnio.length > 0;

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Finanzas</h1>
        <p className="page-subtitle">Estado de resultados · datos confidenciales (solo administrador)</p>
      </div>

      {/* Importador */}
      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 14 }}>Importar planilla</h3>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" style={{ fontSize: 11 }}>Año de la planilla</label>
            <input type="number" min="2000" max="2099" value={importYear} onChange={e => setImportYear(e.target.value)} style={{ width: 110 }} />
          </div>
          <button className="btn btn-secondary" onClick={() => fileRef.current?.click()}><Upload size={15} /> Elegir archivo CSV</button>
          <input ref={fileRef} type="file" onClick={e => { e.target.value = null; }} accept=".csv" onChange={onFile} style={{ display: 'none' }} />
          <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>Exportá tu planilla de Numbers a CSV (Archivo → Exportar a → CSV) y subila acá.</span>
        </div>

        {preview && (
          <div style={{ marginTop: 16, padding: 16, background: 'var(--bg-input)', borderRadius: 10 }}>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>Vista previa — año {importYear} ({preview.records.length} registros)</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
              {Object.entries(preview.resumen).map(([k, v]) => {
                const [sec, cur] = k.split('-');
                const label = sec === 'ingreso' ? 'Ingresos' : sec === 'salario' ? 'Salarios' : 'Gastos';
                return <span key={k} style={{ fontSize: 12.5, padding: '5px 12px', borderRadius: 8, background: 'var(--bg-card)' }}>{label} {cur}: <strong>{fmt(v, cur)}</strong></span>;
              })}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 12 }}>
              ⚠️ Importar reemplazará todos los datos que ya existan del año {importYear}.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={confirmarImport} disabled={importing}>{importing ? 'Importando...' : 'Confirmar importación'}</button>
              <button className="btn btn-secondary" onClick={() => setPreview(null)}>Cancelar</button>
            </div>
          </div>
        )}
      </div>

      {years.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 50, color: 'var(--text-muted)' }}>
          <Wallet size={40} style={{ opacity: 0.4, marginBottom: 12 }} />
          <p>Todavía no importaste datos financieros.</p>
          <p style={{ fontSize: 13 }}>Subí tu primera planilla arriba para empezar.</p>
        </div>
      ) : (
        <>
          {/* Selector de año */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Año:</span>
            {years.map(y => (
              <button key={y} onClick={() => setSelectedYear(y)}
                className={`btn btn-sm ${Number(yearActivo) === y ? 'btn-primary' : 'btn-secondary'}`}>{y}</button>
            ))}
            <button className="btn btn-danger btn-sm" style={{ marginLeft: 'auto' }} onClick={() => borrarAnio(yearActivo)}><Trash2 size={12} /> Borrar año {yearActivo}</button>
          </div>

          {!hayDatos ? (
            <p style={{ color: 'var(--text-muted)' }}>No hay datos para el año {yearActivo}.</p>
          ) : (
            <>
              {/* Resumen anual */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 24 }}>
                <div className="card" style={{ padding: 18 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Ingresos en pesos</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--accent)', marginTop: 6 }}>{fmt(ingresoUYU, 'UYU')}</div>
                </div>
                <div className="card" style={{ padding: 18 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Ingresos en dólares</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#60a5fa', marginTop: 6 }}>{fmt(ingresoUSD, 'USD')}</div>
                </div>
                <div className="card" style={{ padding: 18 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Salarios (pesos)</div>
                  <div style={{ fontSize: 24, fontWeight: 800, marginTop: 6 }}>{fmt(salariosUYU, 'UYU')}</div>
                </div>
                <div className="card" style={{ padding: 18 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Gastos (pesos)</div>
                  <div style={{ fontSize: 24, fontWeight: 800, marginTop: 6 }}>{fmt(gastosUYU, 'UYU')}</div>
                </div>
                <div className="card" style={{ padding: 18, border: '1px solid var(--accent)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Renta neta (pesos)</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: rentaUYU >= 0 ? 'var(--accent)' : 'var(--red)', marginTop: 6 }}>{fmt(rentaUYU, 'UYU')}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 2 }}>ingresos − salarios − gastos</div>
                </div>
              </div>

              {/* Evolución mensual pesos */}
              <div className="card" style={{ padding: 20, marginBottom: 20 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Evolución mensual en pesos — {yearActivo}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={mensualUYU}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a3550" />
                    <XAxis dataKey="mes" tick={{ fill: '#8892b0', fontSize: 12 }} />
                    <YAxis tick={{ fill: '#8892b0', fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={v => fmt(v, 'UYU')} />
                    <Legend />
                    <Bar dataKey="Ingresos" fill="#34d399" radius={[3,3,0,0]} />
                    <Bar dataKey="Egresos" fill="#f87171" radius={[3,3,0,0]} />
                    <Bar dataKey="Renta" fill="#60a5fa" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Ingresos en dólares mensual */}
              {ingresoUSD > 0 && (
                <div className="card" style={{ padding: 20, marginBottom: 20 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Ingresos en dólares por mes — {yearActivo}</h3>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={mensualUSD}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a3550" />
                      <XAxis dataKey="mes" tick={{ fill: '#8892b0', fontSize: 12 }} />
                      <YAxis tick={{ fill: '#8892b0', fontSize: 11 }} />
                      <Tooltip formatter={v => fmt(v, 'USD')} />
                      <Bar dataKey="Ingresos" fill="#60a5fa" radius={[3,3,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Desgloses */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                <div className="card" style={{ padding: 20 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Ingresos por compañía (pesos)</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {porCompaniaUYU.map(c => (
                      <div key={c.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
                        <span>{c.name}</span><strong>{fmt(c.total, 'UYU')}</strong>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="card" style={{ padding: 20 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Ingresos por compañía (dólares)</h3>
                  {porCompaniaUSD.length === 0 ? <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Sin ingresos en dólares.</p> : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {porCompaniaUSD.map(c => (
                        <div key={c.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
                          <span>{c.name}</span><strong>{fmt(c.total, 'USD')}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Desglose de egresos */}
              <div className="card" style={{ padding: 20, marginBottom: 20 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Salarios y gastos (pesos)</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6 }}>
                  {desgloseGastos.map(g => (
                    <div key={g.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '5px 10px', background: 'var(--bg-input)', borderRadius: 6 }}>
                      <span style={{ color: 'var(--text-muted)' }}>{g.name}</span><strong>{fmt(g.total, 'UYU')}</strong>
                    </div>
                  ))}
                </div>
              </div>

              {/* Comparación entre años */}
              {comparacion.length > 1 && (
                <div className="card" style={{ padding: 20 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Comparación entre años (pesos)</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={comparacion}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a3550" />
                      <XAxis dataKey="year" tick={{ fill: '#8892b0', fontSize: 12 }} />
                      <YAxis tick={{ fill: '#8892b0', fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                      <Tooltip formatter={v => fmt(v, 'UYU')} />
                      <Legend />
                      <Line type="monotone" dataKey="Ingresos" stroke="#34d399" strokeWidth={2} />
                      <Line type="monotone" dataKey="Renta" stroke="#60a5fa" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
